import React, { useState, useCallback, useLayoutEffect, useImperativeHandle, useRef } from 'react';
import { useEffect } from 'react';

import colors from 'utils/colors';

import { ComponentProps, AccordionHandle } from './interfaces';
import * as Styled from './styled';

const getId = (id: string) => `acc-${id}`;

export const Accordion = React.forwardRef<AccordionHandle, ComponentProps>(function Accordion(
  {
    items = [],
    defaultActiveIndex = 0,
    allowMultiple,
    onCollapse,
    onExpand,
    expandIcon,
    contractIcon,
    disableUpdateStateOnClick,
    activeIndices: activeIndicesProps,
    accordionItemRaisedOnActive,
    ...props
  },
  ref
) {
  const [activeIndices, setActiveIndices] = useState<number[]>(
    Array.isArray(defaultActiveIndex) ? defaultActiveIndex : [defaultActiveIndex]
  );
  const [scrollRestore, setScrollRestore] = useState<{
    value: number;
    element: HTMLButtonElement;
  }>();

  const [isFocused, setIsFocused] = useState<boolean>(false);

  const setActiveItem = useCallback(
    (evt: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, itemIndex) => {
      const isActive = activeIndices.indexOf(itemIndex) > -1;
      if (!allowMultiple) {
        if (!disableUpdateStateOnClick) {
          setActiveIndices(isActive ? [] : [itemIndex]);
        }

        if (activeIndices.length && onCollapse) {
          onCollapse(activeIndices[0]);
        }

        if (!isActive && onExpand) {
          onExpand(itemIndex);
        }

        /**
         * When not allowing multiple items to be open, the existing accordion item gets closed
         * When this occurs, if there is a lot of content in there the new content will appear partially or full scrolled
         * This is because the content gets removed, shifted up in the DOM & then pushed down
         *
         * We can solve this by:
         * - storing the current scroll offset of the element that is clicked
         * - Comparing the new scroll position of the element after the accordion has closed to the value
         * - If it is in the negatives (scrolled too far up): Offset the value by what it is and where it is supposed to be
         *
         * Key parts for this - storing the value here, and restoring it in the `useLayoutEffect` below
         */
        if (evt !== null) {
          const { top } = evt.currentTarget.getBoundingClientRect();
          setScrollRestore({ value: top, element: evt.currentTarget });
        }
      } else {
        if (!disableUpdateStateOnClick) {
          setActiveIndices(
            isActive
              ? activeIndices.filter(idx => idx !== itemIndex)
              : [...activeIndices, itemIndex]
          );
        }

        if (isActive && onCollapse) {
          onCollapse(itemIndex);
        }

        if (!isActive && onExpand) {
          onExpand(itemIndex);
        }
      }
      if (isActive && activeIndices.length === 1) {
        const element = document.activeElement as HTMLButtonElement;
        element.blur();
      }
    },
    [activeIndices, allowMultiple, disableUpdateStateOnClick, onCollapse, onExpand]
  );

  useEffect(() => {
    if (activeIndicesProps) {
      setActiveIndices(activeIndicesProps);
    }
  }, [activeIndicesProps]);

  const getAccordionHandle = useCallback(
    (): AccordionHandle => ({
      toggle: idx => setActiveItem(null, idx),
      closeAll: () => {
        setActiveIndices([]);

        if (onCollapse) {
          activeIndices.forEach(index => {
            onCollapse(index);
          });
        }
      },
      close: idx => {
        setActiveIndices(indices => indices.filter(i => i !== idx));

        if (onCollapse && activeIndices.includes(idx)) {
          onCollapse(idx);
        }
      },
      open: idx => {
        setActiveIndices(indices => (allowMultiple ? [...indices, idx] : [idx]));

        if (onExpand && !activeIndices.includes(idx)) {
          onExpand(idx);
        }
      }
    }),
    [setActiveItem, setActiveIndices, allowMultiple, onCollapse, onExpand, activeIndices]
  );

  const handle = useRef<AccordionHandle>(getAccordionHandle());

  // a useLayoutEffect is required here so that the handle instance is updated before the render
  //   else it remains 1 render behind what is the correct index
  useLayoutEffect(() => {
    handle.current = getAccordionHandle();
  }, [getAccordionHandle]);

  useImperativeHandle(ref, () => handle.current);

  useLayoutEffect(() => {
    // This is required as a useLayoutEffect so that it is processed before the renders are flushed to the DOM
    // Else the users will get a slight flicker
    if (!scrollRestore) {
      return;
    }
    const newScroll = scrollRestore.element.getBoundingClientRect().top;

    if (newScroll < 0 && document?.scrollingElement) {
      try {
        document.scrollingElement.scrollBy({ top: newScroll - scrollRestore.value });
      } catch (e) {
        // no-op
        // There was an error in datadog that scrollBy didn't exist on element null
        // Wrapping this in a try/catch with no need to do further processing as it's
        // more just a nice-to-have than core functionality
      }
    }
  }, [scrollRestore]);

  const handleKeypress = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // data-controls only exists for accordion handlers
      if (document.activeElement?.getAttribute('data-controls')) {
        let targetIndex: number | undefined = undefined;
        const key = event.which;

        // handle up/pageUp key
        if (key === 33 || key === 38) {
          targetIndex =
            items.findIndex(item => getId(item.id) === document.activeElement?.getAttribute('id')) -
            1;
          // handle down/pageDown keys
        } else if (key === 34 || key === 40) {
          targetIndex =
            items.findIndex(item => getId(item.id) === document.activeElement?.getAttribute('id')) +
            1;
        } else if (key === 35) {
          // end key goes to last element
          targetIndex = items.length - 1;
        } else if (key === 36) {
          // home key goes to first element
          targetIndex = 0;
        }

        if (targetIndex !== undefined) {
          // targetIndex + length allows the up/pgUp key to go to the last element in the list
          const id = getId(items[(targetIndex + items.length) % items.length].id);
          document.getElementById(id)?.focus();

          // only need to prevent the default if we intercepted it
          event.preventDefault();
        }
      }
    },
    [items]
  );

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  return (
    <Styled.Wrapper {...props}>
      <Styled.Accordions onKeyDown={handleKeypress} focused={isFocused}>
        {items.map((item, index) => {
          const isActive = activeIndices.indexOf(index) > -1;

          const derivedExpand = item.expandIcon || expandIcon;
          const derivedContract = item.contractIcon || contractIcon;

          return (
            <Styled.AccordionItem
              key={item.id}
              active={isActive}
              data-raised={accordionItemRaisedOnActive && isActive}>
              <Styled.Header
                onFocus={handleFocus}
                onBlur={handleBlur}
                onClick={e => setActiveItem(e, index)}
                aria-expanded={isActive}
                data-controls={`sect-${item.id}`}
                id={getId(item.id)}>
                <Styled.Title>
                  {typeof item.title === 'function' ? item.title({ isActive }) : item.title}
                </Styled.Title>
                <Styled.IconGroup>
                  {isActive ? (
                    <Styled.IconMinus
                      fill={colors.brown}
                      {...(derivedContract ? { as: derivedContract } : {})}
                    />
                  ) : (
                    <Styled.IconPlus
                      fill={colors.orange}
                      {...(derivedExpand ? { as: derivedExpand } : {})}
                    />
                  )}
                </Styled.IconGroup>
              </Styled.Header>
              <Styled.Content
                id={`sect-${item.id}`}
                role='region'
                aria-labelledby={getId(item.id)}
                hidden={!isActive}>
                {typeof item.component === 'function'
                  ? item.component({ ...handle.current, isActive }, index)
                  : item.component}
              </Styled.Content>
            </Styled.AccordionItem>
          );
        })}
      </Styled.Accordions>
    </Styled.Wrapper>
  );
});

export default Accordion;
